import * as Popover from "@radix-ui/react-popover";
import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Film, ImagePlus, Loader2, Play, Search, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

interface Media {
  id: string;
  filename: string;
  url: string;
  type: string;
  mime_type: string | null;
  size: number | null;
  alt_text: string | null;
}

interface MediaPickerPopoverProps {
  onSelect: (url: string, type: "image" | "video") => void;
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom" | "left" | "right";
  mediaType?: "image" | "video" | "all";
  /** Controlled open state */
  open?: boolean;
  /** Called when open state changes */
  onOpenChange?: (open: boolean) => void;
}

export function MediaPickerPopover({
  onSelect,
  children,
  align = "start",
  side = "bottom",
  mediaType = "all",
  open: controlledOpen,
  onOpenChange,
}: MediaPickerPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen;
  const [searchQuery, setSearchQuery] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState<"image" | "video">(
    mediaType === "video" ? "video" : "image"
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();

  // Get the app root for portal container (so Tailwind styles work)
  useEffect(() => {
    setPortalContainer(document.getElementById("root"));
  }, []);

  // Fetch media
  const { data: mediaData, isLoading } = useQuery({
    queryKey: ["admin-media"],
    queryFn: async () => {
      const res = await authenticatedFetch("/api/admin/media");
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json() as Promise<{ data: Media[] }>;
    },
    enabled: open, // Only fetch when popover is open
  });

  // Upload mutation - accepts File[] to avoid reference issues
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const res = await authenticatedFetch("/api/admin/media/upload", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const error = (await res.json()) as { message?: string };
        throw new Error(error.message || "Failed to upload");
      }
      return res.json() as Promise<{ data: Media[] }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-media"] });
      const uploadedType = data.data[0]?.type as "image" | "video";
      toast.success(`${uploadedType === "video" ? "Video" : "Image"} uploaded`);
      // Auto-select the first uploaded file
      if (data.data.length > 0) {
        handleSelect(data.data[0].url, uploadedType);
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to upload";
      toast.error(message);
    },
  });

  const handleSelect = (url: string, type: "image" | "video") => {
    onSelect(url, type);
    setOpen(false);
    setSearchQuery("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      // IMPORTANT: Convert FileList to Array BEFORE clearing input!
      // FileList is a live reference that gets cleared when input is reset
      const filesArray = Array.from(fileList);
      e.target.value = "";
      uploadMutation.mutate(filesArray);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        // Filter to images and videos and convert to Array
        const mediaFiles = Array.from(files).filter(
          (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
        );
        if (mediaFiles.length > 0) {
          uploadMutation.mutate(mediaFiles);
        }
      }
    },
    [uploadMutation]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Filter media by type and search
  const filteredMedia = (mediaData?.data || []).filter((m) => {
    const matchesType = mediaType === "all" ? m.type === activeTab : m.type === mediaType;
    const matchesSearch = m.filename.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const images = (mediaData?.data || []).filter((m) => m.type === "image");
  const videos = (mediaData?.data || []).filter((m) => m.type === "video");

  const showTabs = mediaType === "all";
  const acceptType =
    mediaType === "image" ? "image/*" : mediaType === "video" ? "video/*" : "image/*,video/*";
  const emptyIcon =
    activeTab === "video" || mediaType === "video" ? (
      <Film className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
    ) : (
      <ImagePlus className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
    );
  const emptyText = activeTab === "video" || mediaType === "video" ? "No videos" : "No images";

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal container={portalContainer}>
        <Popover.Content
          align={align}
          side={side}
          sideOffset={8}
          className="z-50 w-80 bg-card rounded-lg shadow-xl border border-border overflow-hidden relative"
        >
          {/* Header with search */}
          <div className="p-3 border-b border-border">
            {/* Tabs for image/video */}
            {showTabs && (
              <div className="flex gap-1 mb-3">
                <button
                  type="button"
                  onClick={() => setActiveTab("image")}
                  className={cn(
                    "flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                    activeTab === "image"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground hover:text-foreground"
                  )}
                >
                  Images ({images.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("video")}
                  className={cn(
                    "flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                    activeTab === "video"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground hover:text-foreground"
                  )}
                >
                  Videos ({videos.length})
                </button>
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={`Search ${activeTab === "video" ? "videos" : "images"}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm bg-secondary border-0 rounded-md focus:ring-2 focus:ring-ring focus:outline-none"
              />
            </div>
          </div>

          {/* Content area */}
          <div
            className="p-3 max-h-[280px] overflow-y-auto"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {/* Upload zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-4 text-center mb-3 transition-colors",
                isDragging ? "border-primary bg-primary/5" : "border-border bg-secondary/50"
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptType}
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              {uploadMutation.isPending ? (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-2">
                    {isDragging ? "Drop file here" : "Drop file here or"}
                  </p>
                  {!isDragging && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5"
                    >
                      <Upload className="h-3 w-3" />
                      Browse Files
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Media grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filteredMedia.length === 0 ? (
              <div className="text-center py-6">
                {emptyIcon}
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? `${emptyText} found` : `${emptyText} uploaded yet`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {filteredMedia.map((media) => (
                  <button
                    key={media.id}
                    type="button"
                    onClick={() => handleSelect(media.url, media.type as "image" | "video")}
                    className="aspect-square rounded-md overflow-hidden bg-secondary border-2 border-transparent hover:border-primary transition-colors focus:outline-none focus:border-primary relative group"
                  >
                    {media.type === "video" ? (
                      <>
                        <video
                          src={media.url}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <Play className="h-6 w-6 text-white fill-white" />
                        </div>
                      </>
                    ) : (
                      <img
                        src={media.url}
                        alt={media.alt_text || media.filename}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Close button */}
          <Popover.Close className="absolute top-2 right-2 p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors">
            <X className="h-4 w-4" />
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
